/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useState} from 'react';
import {NotificationPanel} from '@ion/applications/src/notifications/NotificationPanel';
import {ToastNotificationsContainer} from '@ion/applications/src/notifications/ToastNotificationsContainer';

/**
 * The three-dot menu entries ADM has no use for - `ClearAll` is the only one
 * left standing.
 *
 * Everything read-related is omitted because ADM's panel holds job status
 * notifications, whose read state is owned by user preferences - the shared
 * mark-as-read actions post to `acknowledgeAlerts`, an alert-only endpoint that
 * has no business seeing a job id. `SeeUnreadOnly` goes with them: a filter on
 * read state is meaningless when nothing in the panel is ever marked read.
 *
 * The alert-panel controls - `Export`, `Filter`, `Reset` and
 * `IncludeSubAccount` - are omitted because they drive the alert polling worker
 * and the alert CSV, neither of which ADM has.
 *
 * `SelectMultiple` is omitted because selection needs no opt-in here - a card
 * click already selects it, which is what raises the panel's selection header
 * and its clear/select-all actions.
 */
export enum MenuItemKeys {
  Export = 'Export',
  Filter = 'Filter',
  Reset = 'Reset',
  MarkAllRead = 'MarkAllRead',
  MarkSelectedRead = 'MarkSelectedRead',
  IncludeSubAccount = 'IncludeSubAccount',
  SeeUnreadOnly = 'SeeUnreadOnly',
  SelectMultiple = 'SelectMultiple'
};

/**
 * @ToastNotificationWrapper
 *
 * Defines a component to display Notification Panel and Toast notification Container.
 *
 * ```typescript
 *   import ToastNotificationWrapper from '@ion/packages/ion-ctc-admin/src/header/ToastNotificationWrapper';
 *
 *   <ToastNotificationWrapper />;
 *
 * ```
 */
const ToastNotificationWrapper = () => {
  const [cancelToastDismiss, setCancelToastDismiss] = useState([]);
  return (
    <>
      <NotificationPanel omittedMenuItems={MenuItemKeys}
        omitUrgentTab
        cancelToastDismiss={cancelToastDismiss}
        setCancelToastDismiss={setCancelToastDismiss} />
      <ToastNotificationsContainer cancelToastDismiss={cancelToastDismiss}
        setCancelToastDismiss={setCancelToastDismiss} />
    </>
  );
};

export default React.memo(ToastNotificationWrapper);
